import React, { useRef } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import PromoTitle from "./PromoTitle";
import TargetingConditions from "./TargetingConditions";
import OfferType from "./OfferType";
import PromoFooter from "./PromoFooter";
import { useCreatePromoState } from "./hooks/useCreatePromoState";
import { validateDiscountAmounts } from "./utils/discountValidation";
import { P, usePermission } from "../../rbac";
import lockAmberIcon from "../../assets/img/lock-amber.svg";
import { CAMPAIGN_PROMO_READONLY_MESSAGE } from "./constants";
import {
  isCampaignPromo,
  isOmnibeesExternalPromo,
} from "../../pages/promotions/utils";
import "./create-promo.scss";

const CreatePromo = ({
  promo,
  setPromo,
  ratePlans,
  rooms,
  ratePlansPropertyId = null,
  isEdit,
  isMarket,
  hideModal,
  setLoading,
  calculationType,
  setCalculationType,
  isDuplicate = false,
  isViewOnly = false,
  isPilotHotel = false,
  onRecommendationUsed = null,
}) => {
  const { allowed: canManagePromo } = usePermission(P.Promotions.Manage);
  const isGcCreated = promo?._derivedRateMeta?.isGcCreated === true;
  const isCampaign = isCampaignPromo(promo);
  // Promos sourced elsewhere (GC pilot / Omnibees) explain themselves with a
  // banner and hide the footer entirely. A missing promo.manage grant instead
  // keeps the footer visible but inert, so the user sees why nothing saves.
  const isSourceReadOnly = isGcCreated || isViewOnly;
  const isReadOnly = isSourceReadOnly || !canManagePromo;
  const isDraftPromo =
    promo?._derivedRateMeta?.status === "DRAFT" || promo?.status === "Draft";
  const targetingConditionsRef = useRef(null);

  const {
    selectedProperty,
    isLaunchDisabled,
    isDraftDisabled,
    tc,
    setTc,
    handleFieldChange,
    submitPromo,
  } = useCreatePromoState({
    promo,
    setPromo,
    ratePlans,
    rooms,
    ratePlansPropertyId,
    isEdit,
    isMarket,
    hideModal,
    setLoading,
    calculationType,
    isDuplicate,
    isPilotHotel,
    onRecommendationUsed,
  });

  // The banner says WHY the promo is read-only, so it shows only when there is a
  // source to name: a GC pilot promo, a Campaign, or an Omnibees (CRS) promo. A promo
  // simply opened for viewing — from its name (EO-1538), or by a user without
  // promo.manage — is read-only without one.
  const isOmnibees = isOmnibeesExternalPromo(promo, selectedProperty);
  const showReadonlyBanner = isGcCreated || isCampaign || isOmnibees;

  let readonlyBannerMessage;
  if (isGcCreated) {
    readonlyBannerMessage = (
      <>
        <span>
          This promotion is part of a Hotel Trader pilot and is managed by our
          team.
        </span>
        <br />
        <span>
          For questions or change requests, please contact your account manager.
        </span>
      </>
    );
  } else if (isCampaign) {
    readonlyBannerMessage = <span>{CAMPAIGN_PROMO_READONLY_MESSAGE}</span>;
  } else {
    readonlyBannerMessage = (
      <span>
        For promos created in Omnibees (CRS). Editing is disabled in Hotel
        Trader.
      </span>
    );
  }

  const handleSubmit = (isDraft) => {
    if (!canManagePromo) {
      return;
    }

    // Validate the discount amounts and the targeting conditions before
    // submitting. Drafts are checked too: an over-100% discount is invalid
    // whether it is launched now or saved for later.
    const validationErrors = [
      ...validateDiscountAmounts(promo),
      ...(targetingConditionsRef.current
        ? targetingConditionsRef.current.validateTargetingConditions(tc)
        : []),
    ];

    if (validationErrors.length > 0) {
      // Format error message based on number of errors
      let errorMessage;
      if (validationErrors.length === 1) {
        // Single error: no bullet point
        errorMessage = validationErrors[0];
      } else {
        // Multiple errors: use bullet points
        errorMessage = `• ${validationErrors.join("\n• ")}`;
      }
      toast.error(errorMessage, {
        style: { whiteSpace: "pre-line" },
      });
      return; // Prevent submission
    }

    // If validation passes, proceed with submission
    submitPromo(isDraft);
  };

  return (
    <div className="create-promo">
      <div className="create-promo-body create-promo-single-view">
        {/* Read-only banner (GC-created, Campaign, or Omnibees) */}
        {isSourceReadOnly && showReadonlyBanner && (
          <div
            data-testid="readonly-banner"
            className={`gc-readonly-banner${
              isGcCreated ? "" : " gc-readonly-banner--warning"
            }`}
          >
            {isGcCreated ? (
              <i className="fa fa-lock gc-readonly-banner-icon" />
            ) : (
              <span className="gc-readonly-banner-icon-wrap">
                <img src={lockAmberIcon} alt="" width="18" height="18" />
              </span>
            )}
            <div className="gc-readonly-banner-content">
              {readonlyBannerMessage}
            </div>
          </div>
        )}

        <fieldset
          disabled={isReadOnly}
          style={{ border: "none", padding: 0, margin: 0 }}
        >
          <div className={isReadOnly ? "create-promo-disabled" : ""}>
            {/* Name Section */}
            <div className="form-section">
              <PromoTitle
                setIsNextBtnDisabled={() => {}}
                fieldValue={promo?.rateRule?.ruleVO?.descriptionVO?.name || ""}
                onFieldChange={handleFieldChange}
              />
            </div>

            {/* Targeting Conditions Section */}
            <div className="form-section">
              <h3 className="form-section-title tc">Targeting Conditions</h3>
              <TargetingConditions
                ref={targetingConditionsRef}
                tc={tc}
                setTc={setTc}
                promo={promo}
                handleFieldChange={handleFieldChange}
                setIsNextBtnDisabled={() => {}}
                isPilotHotel={isPilotHotel}
                isEdit={isEdit}
                isDuplicate={isDuplicate}
              />
            </div>

            {/* Offer Type Section */}
            {promo && (
              <div className="form-section">
                <h3 className="form-section-title">Price Adjustment</h3>
                <OfferType
                  promo={promo}
                  onFieldChange={handleFieldChange}
                  setIsNextBtnDisabled={() => {}}
                  calculationType={calculationType}
                  setCalculationType={setCalculationType}
                  crsId={selectedProperty.crsId}
                  isEdit={isEdit}
                  isDuplicate={isDuplicate}
                  isMarket={isMarket}
                  ratePlans={ratePlans}
                  rooms={rooms}
                  ratePlansPropertyId={ratePlansPropertyId}
                  isReadOnly={isReadOnly}
                  onViewRoomsRateplans={() => {
                    // This callback is no longer needed since OfferType handles opening the drawer directly
                    // Keeping it for backward compatibility but it won't be called in view mode anymore
                  }}
                />
              </div>
            )}
          </div>
        </fieldset>

        {/* Outside the fieldset so its buttons answer to `isReadOnly` rather than to
            the fieldset. A source-read-only promo (GC / Campaign / Omnibees) and any
            promo opened to view (EO-1538) render no footer at all — nothing there can
            be used — and leave by the drawer's ✕ or Escape. Without promo.manage the
            footer still renders, inert, so the user sees why nothing saves. */}
        {promo && !isSourceReadOnly && (
          <PromoFooter
            onCancel={hideModal}
            onSubmit={handleSubmit}
            isLaunchDisabled={isLaunchDisabled || !canManagePromo}
            isDraftDisabled={isDraftDisabled || !canManagePromo}
            isMarket={isMarket}
            isEdit={isEdit}
            isDuplicate={isDuplicate}
            isDraftPromo={isDraftPromo}
            isPilotHotel={isPilotHotel}
          />
        )}
      </div>
    </div>
  );
};

CreatePromo.propTypes = {
  promo: PropTypes.shape({
    rateRule: PropTypes.shape({
      ruleVO: PropTypes.object, //rateRuleVO can have different properties based on the promo configuration
    }),
    _derivedRateMeta: PropTypes.shape({
      isGcCreated: PropTypes.bool,
      status: PropTypes.string,
    }),
  }),
  setPromo: PropTypes.func,
  ratePlans: PropTypes.array,
  rooms: PropTypes.array,
  ratePlansPropertyId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  isEdit: PropTypes.bool,
  isMarket: PropTypes.bool,
  hideModal: PropTypes.func,
  setLoading: PropTypes.func,
  calculationType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setCalculationType: PropTypes.func,
  isDuplicate: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  isPilotHotel: PropTypes.bool,
  onRecommendationUsed: PropTypes.func,
};

export default CreatePromo;
